import type { BrowserOptions, ErrorEvent, EventHint } from "@sentry/react";
import GrailedAPIError from "storefront/GrailedAPI/Error";
import GrailedAPIFetchError from "storefront/GrailedAPI/FetchError";
import {
  fingerprintGrailedAPIError,
  fingerprintGrailedAPIFetchError,
} from "storefront/lib/fingerprintGrailedAPIError";
import { grailedAPIErrorSource } from "storefront/lib/grailedAPIErrorSource";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

// The Algolia client tries every host before rejecting with a RetryError. Status 0 on every attempt
// means no host ever responded (blocked, offline, aborted by navigation, or timed out) — the XHR
// counterpart of the fetch() network failures in ignoreErrors.
const isAlgoliaUnreachable = (exception: unknown): boolean =>
  isRecord(exception) &&
  exception.name === "RetryError" &&
  Array.isArray(exception.transporterStackTrace) &&
  exception.transporterStackTrace.every(
    (attempt: unknown) =>
      isRecord(attempt) &&
      isRecord(attempt.response) &&
      attempt.response.status === 0,
  );

// A browser stuck in a loop (a listener throwing on every pointermove, a chunk that never loads) can
// send hundreds of copies of one error within seconds, exhausting the project's hourly quota and
// dropping every other user's errors until it resets. The first copies carry everything later ones
// would. The count lives in module scope, so it resets on each full page load.
const MAX_REPORTS_PER_ERROR = 10;
const reportCountBySignature = new Map<string, number>();

const errorSignature = (event: ErrorEvent): string | undefined => {
  const exceptions = event.exception?.values;
  const outermost = exceptions?.[exceptions.length - 1];

  return outermost ? `${outermost.type}: ${outermost.value}` : event.message;
};

const capRepeats = (event: ErrorEvent): ErrorEvent | null => {
  const signature = errorSignature(event);
  if (!signature) return event;

  const count = (reportCountBySignature.get(signature) ?? 0) + 1;
  reportCountBySignature.set(signature, count);

  if (count > MAX_REPORTS_PER_ERROR) return null;
  if (count < MAX_REPORTS_PER_ERROR) return event;

  return { ...event, tags: { ...event.tags, repeatsCapped: true } };
};

const filterAndEnrich = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  const exception = hint?.originalException;

  if (exception instanceof GrailedAPIFetchError) {
    const fingerprint = fingerprintGrailedAPIFetchError(exception);
    return fingerprint ? { ...event, fingerprint } : event;
  }

  if (isAlgoliaUnreachable(exception)) return null;
  if (!(exception instanceof GrailedAPIError)) return event;

  const fingerprint = fingerprintGrailedAPIError(exception);
  const { origin, rayId, contentType } = grailedAPIErrorSource(exception);

  return {
    ...event,
    ...(fingerprint ? { fingerprint } : {}),
    tags: { ...event.tags, apiErrorSource: origin },
    contexts: {
      ...event.contexts,
      apiError: { source: origin, cfRay: rayId, contentType },
    },
  };
};

export const beforeSend = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  const filtered = filterAndEnrich(event, hint);
  return filtered && capRepeats(filtered);
};

/**
 * The storefront and the legacy client report to separate Sentry projects, but both throw the same
 * GrailedAPIError from the same request layer, so they see the same errors and want the same
 * filtering and enrichment. Spread these into each app's own init, which keeps the settings that
 * genuinely differ — dsn, environment, release, breadcrumb depth. Spread first: a per-app
 * `ignoreErrors` or `denyUrls` replaces the array here rather than extending it.
 */
export const sharedSentryOptions: Pick<
  BrowserOptions,
  "ignoreErrors" | "denyUrls" | "beforeSend"
> = {
  ignoreErrors: [
    /TypeError: t is not a function. \(In 't\(\)', 't' is undefined\)$/,

    // Noise from third-party/extension code calling global logMutedMessage in page context
    /^ReferenceError: Can't find variable: logMutedMessage$/,

    // Injected by Meta's in-app browsers (Instagram, Facebook), unactionable
    /window\.webkit\.messageHandlers/,
    /Error invoking \w+: Java object is gone/,

    // Thrown inside Segment's Bing Ads destination, not our code
    /window\.UET/,

    // Bluebird inside browser extensions (e.g. Honey) dispatches its own unhandledrejection
    // CustomEvent, which reaches the page without a reason or stack
    /^CustomEvent: Event `CustomEvent` \(type=unhandledrejection\) captured as promise rejection$/,

    // A bare {} rejected outside our code (only ever seen on desktop Mac, almost all Safari), with
    // no reason or stack to act on
    /^UnhandledRejection: Object captured as promise rejection with keys: \[object has no keys\]$/,

    // Client-side network failures, where fetch() never received a response
    // (connection dropped, DNS failure, offline, or server reset mid-request).
    // Server-side issues are caught by platform Sentry, Datadog, and New Relic.
    // Either raw from fetch() or wrapped by GrailedAPI's request() as
    // "GET /api/x failed without a response: Failed to fetch". The SDK appends
    // the request host to the raw messages, so allow a suffix. WebKit prefixes
    // uncaught raw rejections with "Unhandled Promise Rejection: ".
    /(^TypeError: |failed without a response: )Failed to fetch( \(.+\))?$/,
    /(^TypeError: (Unhandled Promise Rejection: )?|failed without a response: )Load failed( \(.+\))?$/,
    /(^TypeError: |failed without a response: )NetworkError when attempting to fetch resource\.( \(.+\))?$/,
  ],
  denyUrls: [
    // Errors inside OneTrust's consent banner SDK are not in our control
    /cdn\.cookielaw\.org/,
    /otBannerSdk\.js/,

    // Errors inside Facebook's SDK are not in our control
    /connect\.facebook\.net/,

    // Browser extensions that patch window.fetch / XMLHttpRequest in the page
    // context, then throw on our analytics requests
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-web-extension:\/\//,
    /\/executors\/\d+\.js$/,
  ],
  beforeSend,
};
